/**
 * Formats a monetary value as a currency string with configurable options.
 *
 * @param {number} value - The monetary value to format.
 * @param {string} currency - The currency code (e.g., 'USD', 'EUR', 'GBP').
 * @param {string} [format] - Optional format template string (e.g., "CC$ 0.00", "$0.00", "0.00"). When empty, default currency formatting is used.
 * @param {string | null} zeroString - Custom string to return when value is 0. If null, formats as currency.
 * @param {boolean} isSmallestUnit - If true, converts the value from the smallest unit (e.g., cents) to the major unit (e.g., dollars).
 * @returns {string} - The formatted monetary value as a string.
 *
 * @example
 * format: "CC$ 0.00", return "NZ$ 1.23"
 * format: "CCC$ 0.00", return "NZD$ 1.23"
 * format: "$0.00", return "$1.23"
 * format: "0.00", return "1.23"
 * format: "$0", return "$1"
 */
export function formatPrice(
    value: number,
    currency: string,
    format: string = '',
    zeroString: string | null = null,
    isSmallestUnit: boolean = false,
): string {
    if (value === 0 && zeroString !== null) {
        return zeroString;
    }

    currency = currency.toUpperCase();
    const resultPrice = isSmallestUnit ? value / 100.0 : value;

    // If format is empty, use default currency formatting
    if (!format) {
        const minimumFractionDigits = Number.isInteger(resultPrice) ? 0 : 2;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            minimumFractionDigits,
        }).format(resultPrice);
    }

    // Parse the format string to understand the structure
    const formatMatch = format.match(/^(.*?)(0+(?:\.0+)?)(.*)$/);
    if (!formatMatch) {
        // Fallback to simple number formatting if format is invalid
        return resultPrice.toLocaleString('en-US');
    }

    const numberPattern = formatMatch[2];

    // Ensure numberPattern is defined
    if (!numberPattern) {
        return resultPrice.toLocaleString('en-US');
    }

    // Determine decimal places from the format
    const decimalPlaces = numberPattern.includes('.')
        ? (numberPattern.split('.')[1]?.length ?? 0)
        : 0;

    // Format the number with appropriate decimal places
    const formattedNumber = Math.abs(resultPrice).toLocaleString('en-US', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    });

    // Replace currency placeholders in the format
    let result = format;

    // Replace CCC with full currency code (e.g., NZD, USD, AUD) - do this first to avoid conflicts
    result = result.replace(/CCC/g, currency);

    // Replace CC with currency code (e.g., NZ, US, AU) - do this after CCY
    result = result.replace(/CC/g, currency.substring(0, 2));

    // Replace the number pattern with the actual formatted number
    result = result.replace(numberPattern, formattedNumber);

    // Handle negative numbers by adding minus sign at the beginning
    if (resultPrice < 0) {
        // Find the position after the currency symbol and add the minus sign
        const currencyMatch = result.match(/^([A-Z]*\$)/);
        if (currencyMatch) {
            const currencySymbol = currencyMatch[1];
            if (currencySymbol) {
                const afterCurrency = result.substring(currencySymbol.length);
                result = `-${currencySymbol}${afterCurrency}`;
            } else {
                result = `-${result}`;
            }
        } else {
            result = `-${result}`;
        }
    }

    return result;
}

/**
 * Converts a monetary value to a formatted string representation.
 * Warning: return null if value is less than 1
 *
 * @param {number} value - The monetary value to format.
 * @param {string} currency - The currency code (e.g., 'USD', 'EUR', 'GBP').
 * @param {boolean} isSmallestUnit - If true, converts the value from the smallest unit (e.g., cents) to the major unit (e.g., dollars).
 * @returns {string} - The formatted monetary value as a string.
 */
export function formatPriceForProductTilePrice(
    value: number,
    currency: string,
    isSmallestUnit: boolean = false,
): string | null {
    if (value < 1) {
        return null;
    }

    // TODO: use { minimumFractionDigits: 2, trailingZeroDisplay: 'stripIfInteger' } option for NumberFormat after upgrade to node >= 19
    const resultPrice = isSmallestUnit ? value / 100.0 : value;
    const minimumFractionDigits = Number.isInteger(resultPrice) ? 0 : 2;

    return Intl.NumberFormat('en-NZ', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits,
    }).format(resultPrice);
}
